import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable11 = ({ data }) => {
  const pdfRef = useRef();

  // Extract Values
  const labels = data.map((item) => item.agegrp_label);
  const employmentValues = data.map((item) => Number(item.employment_2024));
  const femalePercentValues = data.map((item) =>
    Number(item.female_percentage),
  );

  // Insights
  const maxIndex = femalePercentValues.indexOf(
    Math.max(...femalePercentValues),
  );
  const minIndex = femalePercentValues.indexOf(
    Math.min(...femalePercentValues),
  );
  const avgFemalePercent = (
    femalePercentValues.reduce((a, b) => a + b, 0) / femalePercentValues.length
  ).toFixed(2);

  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: "Employment in 2024",
        data: employmentValues,
        backgroundColor: "#2C92B850",
        borderColor: "#2C92B8",
        borderWidth: 2,
        yAxisID: "y1",
      },
      {
        type: "line",
        label: "Female Percentage",
        data: femalePercentValues,
        borderColor: "#EC4899",
        backgroundColor: "#EC489930",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 6,
        pointBackgroundColor: "#EC4899",
        yAxisID: "y2",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 14 } },
      },
      title: {
        display: true,
        text: "Employment & Female Participation in 2024 (By Age Group)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y1: {
        title: { display: true, text: "Total Employment" },
        position: "left",
      },
      y2: {
        title: { display: true, text: "Female Percentage (%)" },
        position: "right",
        grid: { drawOnChartArea: false },
      },
      x: {
        title: { display: true, text: "Age Group" },
      },
    },
  };

  // PDF Download
const downloadPDF = () => {
  const input = pdfRef.current;
  html2canvas(input, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;

    const addHeader = () => {
      const logoWidth = 20; // ideal header width
      const logoHeight = 20; // adjust ratio if needed

      pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
      pdf.line(10, 35, 200, 35);
    };

    const addFooter = (pageNumber) => {
      pdf.setFontSize(9);
      const footerY = pageHeight - 10;
      pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        10,
        footerY,
      );
      pdf.text(
        `Page ${pageNumber}`,
        pageWidth - 10,
        footerY,
        null,
        null,
        "right",
      );
    };

    // First page
    addHeader();
    pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
    heightLeft -= pageHeight - 32;

    // Add more pages if needed
    while (heightLeft > 0) {
      pdf.addPage();
      pageNumber++;
      addHeader();
      position = heightLeft - imgHeight;
      pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);
    }

    // Final footer
    // ===== FINAL FOOTER SAFE AREA =====

    const footerTopY = pageHeight - 40; // start footer 40mm from bottom
    const footerContentY = pageHeight - 28; // logos row
    const footerBottomTextY = pageHeight - 10; // bottom text row

    // Top Divider Line
    pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

    // UtahData logo
    const utahWidth = 18;
    const utahHeight = 18;
    pdf.addImage(
      utahData,
      "PNG",
      20,
      footerContentY - 8,
      utahWidth,
      utahHeight,
    );

    // Built by
    pdf.setFontSize(10);
    pdf.text("Built by", 45, footerContentY);

    // BODEX logo
    const bodexWidth = 36;
    const bodexHeight = bodexWidth * 0.3;
    pdf.addImage(bodex, "PNG", 60, footerContentY - 6, bodexWidth, bodexHeight);

    // Using text
    pdf.text("using", 102, footerContentY);

    // KPI logo
    pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

    // Version
    pdf.text("20250916.v3.198", 140, footerContentY);

    // Bottom Info Line
    pdf.setFontSize(8);
    pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
    pdf.text(
      `Downloaded on: ${new Date().toLocaleDateString()}`,
      pageWidth - 10,
      footerBottomTextY,
      null,
      null,
      "right",
    );

    pdf.save("female_participation_2024_agegroup.pdf");
  });
};

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          📊 Employment & Female Participation in 2024 by Age Group
        </h2>

        {/* Summary Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-green-100 shadow text-center">
            <p className="text-gray-600 text-sm">Highest Female Ratio</p>
            <p className="text-xl font-bold text-green-700">
              {labels[maxIndex]} ({femalePercentValues[maxIndex]}%)
            </p>
          </div>

          <div className="p-4 rounded-xl bg-red-100 shadow text-center">
            <p className="text-gray-600 text-sm">Lowest Female Ratio</p>
            <p className="text-xl font-bold text-red-700">
              {labels[minIndex]} ({femalePercentValues[minIndex]}%)
            </p>
          </div>

          <div className="p-4 rounded-xl bg-blue-100 shadow text-center">
            <p className="text-gray-600 text-sm">Average Female %</p>
            <p className="text-xl font-bold text-blue-700">
              {avgFemalePercent}%
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Employment (2024)</th>
                  <th className="p-2">Female %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.agegrp_label}
                    className="hover:bg-gray-100 transition"
                  >
                    <td className="p-2 font-medium">{row.agegrp_label}</td>
                    <td className="p-2 text-blue-700 font-semibold">
                      {Number(row.employment_2024).toLocaleString("en-US")}
                    </td>
                    <td className="p-2 text-red-600 font-bold">
                      {row.female_percentage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Export button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable11;
